import { graphql } from 'react-relay';
import type { FC } from 'react';
import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import EmojiAddIcon from '@atlaskit/icon/core/emoji-add';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import IconButton from '@atlaskit/button/icon/button';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { usePageContentId } from '@confluence/page-context';

import type {
	ReactionsPlaceholderQuery as ReactionsPlaceholderQuery$data,
	ReactionsPlaceholderQueryVariables as ReactionsPlaceholderQuery$variables,
} from './__types__/ReactionsPlaceholderQuery.ts';

const ReactionsPlaceholderQuery = graphql`
	query ReactionsPlaceholderQuery($contentId: ID!) {
		content(id: $contentId) {
			nodes {
				id
				contentReactionsSummary {
					reactionsSummaryForEmoji {
						id
					}
				}
			}
		}
	}
`;
const placeholderContainerStyles = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	cursor: 'not-allowed !important',
	marginTop: token('space.025'),
	marginBottom: token('space.025'),
	marginLeft: '0px',
});

const reactionPillStyles = css({
	width: 42,
	height: 24,
	background: token('color.skeleton.subtle'),
	borderRadius: token('radius.full'),
	marginRight: token('space.050'),
});

const reactionIconStyles = css({
	width: 12,
	height: 12,
	background: token('color.skeleton.subtle'),
	borderRadius: token('radius.full'),
	marginLeft: token('space.075'),
});

type ReactionsPlaceholderProps = {
	useAccuratePills?: boolean;
	onlyRenderPicker?: boolean;
	customAnalyticsAttributes?: Record<string, any>;
};

const DEFAULT_NUM_PILLS = 2;

export const ReactionsPlaceholder: FC<ReactionsPlaceholderProps> = ({
	useAccuratePills,
	onlyRenderPicker,
	customAnalyticsAttributes,
}) => {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = window.__SSR_RENDERED__ || process.env.REACT_SSR;
	const [contentId] = usePageContentId();
	const { data, loading } = useQueryInterop<
		ReactionsPlaceholderQuery$data,
		ReactionsPlaceholderQuery$variables
	>(ReactionsPlaceholderQuery, {
		variables: { contentId: contentId! },
		// Only run this query if we're in SSR and useAccuratePills is set and not showing picker since doesn't use the pill UI
		skip: !isSSR || !useAccuratePills || !contentId || onlyRenderPicker,
	});
	if (loading) {
		return null;
	}

	// Picker is only an icon and has no pills
	const renderPlaceholder = (numPills) =>
		onlyRenderPicker ? (
			<Flex justifyContent="center" alignItems="center" testId="reactions-picker-placeholder">
				<IconButton
					appearance="subtle"
					icon={EmojiAddIcon}
					label=""
					analyticsContext={{
						name: 'reactionsPicker',
						...customAnalyticsAttributes,
					}}
				/>
			</Flex>
		) : (
			<div css={placeholderContainerStyles} data-testid="reactions-placeholder">
				{Array(numPills)
					.fill('')
					.map((_, i) => (
						<span css={reactionPillStyles} key={i} data-testid="reactions-pill" />
					))}
				<span css={reactionIconStyles} data-testid="reaction-icon" />
			</div>
		);

	// We only need to show accurate pills in SSR
	if (isSSR) {
		const numDistinctReactions =
			data?.content?.nodes?.[0]?.contentReactionsSummary?.reactionsSummaryForEmoji.length;

		// If we get a count of reactions use it, otherwise fall back to the default
		const numReactionPills =
			numDistinctReactions !== undefined ? numDistinctReactions : DEFAULT_NUM_PILLS;

		return renderPlaceholder(numReactionPills);
	} else {
		return renderPlaceholder(DEFAULT_NUM_PILLS);
	}
};
